import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Modal from '../../comun/Modal';
import printerService from '../../services/printerService';

const Recepcion = () => {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentShift, setCurrentShift] = useState(null);
    const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
    const [filterStatus, setFilterStatus] = useState('ALL');

    const [showModal, setShowModal] = useState(false);
    const [selectedApt, setSelectedApt] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [amountReceived, setAmountReceived] = useState(0); // COP o USD
    const [discountAmount, setDiscountAmount] = useState(0);

    const getClinicaBaseURL = () => process.env.REACT_APP_API_URL || 'http://localhost:8000';

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            // Cargar Caja Actual
            const currentRes = await api.get('/api/pos/shifts/current/', { baseURL: getClinicaBaseURL() });
            const shiftData = currentRes.data.shift || currentRes.data;
            if (shiftData && shiftData.id) {
                setCurrentShift(shiftData);
            } else {
                setCurrentShift(null);
            }
        } catch (err) {
            setCurrentShift(null);
        }

        try {
            // Cargar Citas
            const aptRes = await api.get('/api/appointments/', { baseURL: getClinicaBaseURL() });
            let allApts = aptRes.data.results || aptRes.data || [];
            
            // Filtrar por fecha
            if (filterDate) {
                allApts = allApts.filter(apt => apt.date === filterDate);
            }
            
            // Filtrar por estado
            if (filterStatus !== 'ALL') {
                allApts = allApts.filter(apt => apt.status === filterStatus);
            }

            setAppointments(allApts);
        } catch (err) {
            console.error('Error fetching appointments:', err);
            setError('Error al cargar las citas.');
        } finally {
            setLoading(false);
        }
    }, [filterDate, filterStatus]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleSelectAppointment = (apt) => {
        setSelectedApt(apt);
        setShowModal(true);
        setDiscountAmount(0);
        setAmountReceived(getPaymentTotals(apt, 0).total);
    };

    const getPaymentTotals = (apt = selectedApt, extraDiscount = discountAmount) => {
        if (!apt) return { subtotal: 0, advance: 0, total: 0 };
        
        let subtotal = 0;
        if (apt.services_data && apt.services_data.length > 0) {
            apt.services_data.forEach(srv => {
                subtotal += parseFloat(srv.price || 0);
            });
        }
        
        // Sumar productos si los hay
        if (apt.products_data && apt.products_data.length > 0) {
            apt.products_data.forEach(prod => {
                subtotal += parseFloat(prod.price || 0) * (prod.quantity || 1);
            });
        }
        
        const advance = parseFloat(apt.advance_payment || 0);
        const total = Math.max(0, subtotal - advance - extraDiscount);
        
        return { subtotal, advance, total };
    };

    const handleCharge = async () => {
        if (!currentShift) {
            alert('Debe abrir la caja (Turno) antes de realizar un cobro.');
            return;
        }

        const totals = getPaymentTotals();
        
        if (totals.subtotal <= 0 && amountReceived <= 0 && discountAmount <= 0) {
            // Solo marcar como completada sin cobro
            await markCompleted(amountReceived, discountAmount);
            return;
        }

        try {
            // 1. Crear el pago asociado al turno actual (Order simulado)
            const orderPayload = {
                shift: currentShift.id,
                customer_name: selectedApt.patient_name,
                order_type: 'dine_in',
                payment_status: 'paid',
                status: 'completed',
                payment_method: paymentMethod,
                total_amount: amountReceived,
                notes: `Cobro Cita #${selectedApt.id} - ${selectedApt.patient_name} | Turno: ${currentShift.shift_number}`,
                items: []
            };

            await api.post('/api/orders/orders/', orderPayload, { baseURL: getClinicaBaseURL() });

            // 2. Actualizar saldo deudor del paciente (siempre, incluso si debt === 0)
            try {
                const patientRes = await api.get(`/api/patients/${selectedApt.patient}/`, { baseURL: getClinicaBaseURL() });
                const currentBalance = parseFloat(patientRes.data.balance || 0);
                // debt > 0: aún queda saldo. debt <= 0: ya pagó completo o de más
                const debt = Math.max(0, totals.total - amountReceived);
                await api.patch(`/api/patients/${selectedApt.patient}/`, {
                    balance: debt
                }, { baseURL: getClinicaBaseURL() });
            } catch (e) {
                console.error('Error actualizando saldo deudor:', e);
            }

            // 3. Marcar la cita como COMPLETADA con el monto cobrado
            await markCompleted(amountReceived, discountAmount);

            // 4. Imprimir ticket de cobro
            printReceipt(totals.total, amountReceived);

        } catch (err) {
            console.error('Error charging:', err);
            if (err.response?.status === 400 && err.response?.data?.items) {
                alert('El sistema POS requiere productos. Asegúrese de tener el módulo configurado.');
            } else {
                alert('Error al registrar el cobro en caja: ' + (err.response?.data ? JSON.stringify(err.response.data) : err.message));
            }
        }
    };

    const markCompleted = async (amountPaid = 0, extraDiscount = 0) => {
        try {
            const patchData = {
                status: 'completed',
                advance_payment: parseFloat(selectedApt.advance_payment || 0) + amountPaid
            };
            if (extraDiscount > 0) {
                const updatedServicesData = [...(selectedApt.services_data || [])];
                updatedServicesData.push({
                    treatment: null,
                    name: 'Descuento en Caja',
                    price: -Math.abs(extraDiscount),
                    notes: 'Aplicado en recepción'
                });
                patchData.services_data = updatedServicesData;
            }
            await api.patch(`/api/appointments/${selectedApt.id}/`, patchData, { baseURL: getClinicaBaseURL() });
            
            setShowModal(false);
            setDiscountAmount(0);
            loadData();
            alert('Cita cobrada y marcada como Completada.');
        } catch (err) {
            console.error('Error updating status:', err);
            alert('Error al actualizar el estado de la cita.');
        }
    };

    const printReceipt = async (totalDisplay, totalCop) => {
        const ticket = `
    BIODENTALITY CLINIC
    --------------------
    RECIBO DE PAGO
    Cita: #${selectedApt.id}
    Paciente: ${selectedApt.patient_name}
    Fecha: ${new Date().toLocaleString()}
    
    Servicios:
    ${selectedApt.services_data?.map(s => `- ${s.name}: $${s.price}`).join('\n')}
    
    Subtotal: $${getPaymentTotals().subtotal.toFixed(2)}
    Abono (Agenda): -$${getPaymentTotals().advance.toFixed(2)}
    TOTAL A PAGAR: $${getPaymentTotals().total.toFixed(2)} USD
    
    MONTO PAGADO HOY: $${amountReceived.toFixed(2)} USD
    DEUDA RESTANTE: $${Math.max(0, getPaymentTotals().total - amountReceived).toFixed(2)} USD
    
    Método de Pago: ${paymentMethod}
    ¡Gracias por su visita!
    `;
        try {
            await printerService.printCustomTicket(ticket, 'receipt');
        } catch (error) {
            console.warn('Impresión omitida:', error.message);
            alert('Aviso: La venta fue registrada pero no se pudo imprimir el recibo porque no hay impresora configurada.');
        }
    };

    const statusTranslations = {
        'scheduled': 'PROGRAMADA', 'SCHEDULED': 'PROGRAMADA',
        'in_progress': 'EN PROCESO', 'IN_PROGRESS': 'EN PROCESO',
        'completed': 'COMPLETADA', 'COMPLETED': 'COMPLETADA',
        'cancelled': 'CANCELADA', 'CANCELLED': 'CANCELADA',
    };

    const isFullyPaid = (apt) => {
        if (!apt) return false;
        const s = (apt.status || '').toUpperCase();
        if (s !== 'COMPLETED') return false;
        let sub = 0;
        if (apt.services_data) apt.services_data.forEach(s2 => sub += parseFloat(s2.price || 0));
        if (apt.products_data) apt.products_data.forEach(p => sub += parseFloat(p.price || 0) * (p.quantity || 1));
        if (sub === 0) return false;
        return parseFloat(apt.advance_payment || 0) >= sub;
    };

    const isPartiallyPaid = (apt) => {
        if (!apt) return false;
        // Tiene abono pero no cubre el total
        const advance = parseFloat(apt.advance_payment || 0);
        if (advance <= 0) return false;
        let sub = 0;
        if (apt.services_data) apt.services_data.forEach(s2 => sub += parseFloat(s2.price || 0));
        if (apt.products_data) apt.products_data.forEach(p => sub += parseFloat(p.price || 0) * (p.quantity || 1));
        return advance > 0 && advance < sub;
    };

    const hasPendingPayment = (apt) => {
        if (!apt) return false;
        let sub = 0;
        if (apt.services_data) apt.services_data.forEach(s2 => sub += parseFloat(s2.price || 0));
        if (apt.products_data) apt.products_data.forEach(p => sub += parseFloat(p.price || 0) * (p.quantity || 1));
        return sub > 0 && !isFullyPaid(apt);
    };

    const getStatusDisplay = (apt) => {
        if (isFullyPaid(apt)) return 'PAGADA';
        if (isPartiallyPaid(apt)) return 'ABONADO';
        const s = (apt.status || '').toUpperCase();
        if (hasPendingPayment(apt)) return 'POR COBRAR';
        return statusTranslations[apt.status] || apt.status;
    };

    const styles = {
        container: { padding: '2rem', backgroundColor: '#f8f9fb', minHeight: '100vh', position: 'relative' },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' },
        title: { margin: 0, fontSize: '1.75rem', color: '#1a1a2e', fontWeight: '800' },
        grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' },
        card: (apt) => {
            const paid = isFullyPaid(apt);
            const partial = isPartiallyPaid(apt);
            const border = paid ? '#10b981' : partial ? '#94a3b8' : hasPendingPayment(apt) ? '#ef4444' : '#3b82f6';
            const bg = paid ? '#f0fdf4' : partial ? '#f8fafc' : '#fff';
            return { backgroundColor: bg, borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', cursor: 'pointer', border: '1px solid #e2e8f0', borderLeft: `6px solid ${border}`, transition: 'transform 0.2s', opacity: paid ? 0.75 : 1 };
        },
        badge: (apt) => {
            if (isFullyPaid(apt)) return { padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '700', backgroundColor: '#dcfce7', color: '#166534' };
            if (isPartiallyPaid(apt)) return { padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '700', backgroundColor: '#e2e8f0', color: '#475569' };
            if (hasPendingPayment(apt)) return { padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '700', backgroundColor: '#fee2e2', color: '#991b1b' };
            return {
                padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '700',
                backgroundColor: '#dbeafe',
                color: '#1e40af'
            };
        },
        filters: { display: 'flex', gap: '1rem', marginBottom: '2rem', backgroundColor: '#fff', padding: '1rem', borderRadius: '12px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' },
        input: { padding: '0.5rem', borderRadius: '8px', border: '1px solid #cbd5e1' },
        buttonPrimary: { padding: '0.75rem 1.5rem', borderRadius: '8px', backgroundColor: '#002b5e', color: '#fff', border: 'none', fontWeight: '700', cursor: 'pointer', width: '100%' }
    };

    const totals = getPaymentTotals();

    return (
        <div style={styles.container}>
            {/* Banner de Caja Cerrada */}
            {!loading && !currentShift && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.85)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9999,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    color: '#1e293b'
                }}>
                    <i className="bi bi-lock-fill" style={{ fontSize: '5rem', color: '#64748b', marginBottom: '1rem' }}></i>
                    <h2 style={{ fontWeight: '800', margin: 0 }}>Caja se encuentra cerrada</h2>
                    <p style={{ fontSize: '1.2rem', color: '#475569', marginTop: '0.5rem', marginBottom: '1.5rem' }}>Por favor abrir caja en el apartado de "Caja / Pagos".</p>
                    <button 
                        onClick={() => navigate('/clinica/shift')}
                        style={{
                            padding: '12px 24px', backgroundColor: '#10b981', color: 'white', 
                            border: 'none', borderRadius: '8px', fontWeight: 'bold', fontSize: '1.1rem',
                            cursor: 'pointer', boxShadow: '0 4px 6px rgba(16, 185, 129, 0.3)'
                        }}
                    >
                        Ir a Caja / Pagos
                    </button>
                </div>
            )}

            <div style={{...styles.header, filter: (!loading && !currentShift) ? 'blur(2px)' : 'none'}}>
                <div>
                    <h1 style={styles.title}>Recepción y Cobros</h1>
                    <p style={{ margin: '0.5rem 0 0', color: '#64748b' }}>
                        {currentShift ? `Caja Abierta (Turno #${currentShift.shift_number})` : ' Caja Cerrada - Debe abrirla en "Caja / Pagos"'}
                    </p>
                </div>
            </div>

            <div style={{...styles.filters, filter: (!loading && !currentShift) ? 'blur(2px)' : 'none', pointerEvents: (!loading && !currentShift) ? 'none' : 'auto'}}>
                <div>
                    <label style={{display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '600'}}>Fecha</label>
                    <input type="date" style={styles.input} value={filterDate} onChange={e => setFilterDate(e.target.value)} />
                </div>
                <div>
                    <label style={{display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', fontWeight: '600'}}>Estado</label>
                    <select style={styles.input} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                        <option value="ALL">Todas</option>
                        <option value="SCHEDULED">Programadas</option>
                        <option value="IN_PROGRESS">En Proceso</option>
                        <option value="COMPLETED">Completadas</option>
                    </select>
                </div>
            </div>

            {loading ? <p>Cargando citas...</p> : (
                <div style={{...styles.grid, filter: (!loading && !currentShift) ? 'blur(2px)' : 'none', pointerEvents: (!loading && !currentShift) ? 'none' : 'auto'}}>
                    {appointments.map(apt => (
                        <div key={apt.id} style={styles.card(apt)} onClick={() => handleSelectAppointment(apt)}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                <strong>{(apt.start_time || apt.time || '00:00:00').substring(0, 5)}</strong>
                                <span style={styles.badge(apt)}>{getStatusDisplay(apt)}</span>
                            </div>
                            <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.1rem', color: isFullyPaid(apt) ? '#64748b' : '#0f172a' }}>{apt.patient_name}</h3>
                            <p style={{ margin: '0 0 0.5rem 0', color: '#94a3b8', fontSize: '0.85rem' }}>
                                <strong>C.I:</strong> {apt.patient_cedula || 'N/A'}
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '0.5rem' }}>
                                <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9rem' }}>
                                    Dr. {apt.doctor_name || 'Asignado'}
                                </p>
                                {(getPaymentTotals(apt).subtotal > 0) && (
                                    <div style={{ textAlign: 'right' }}>
                                        {isPartiallyPaid(apt) && (
                                            <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Abonado: ${parseFloat(apt.advance_payment || 0).toFixed(2)}</div>
                                        )}
                                        <strong style={{ color: isFullyPaid(apt) ? '#166534' : isPartiallyPaid(apt) ? '#475569' : '#991b1b', fontSize: '1.1rem' }}>
                                            ${isPartiallyPaid(apt)
                                                ? (getPaymentTotals(apt).subtotal - parseFloat(apt.advance_payment || 0)).toFixed(2)
                                                : getPaymentTotals(apt).subtotal.toFixed(2)}
                                            {isPartiallyPaid(apt) && <span style={{ fontSize: '0.75rem', fontWeight: '400' }}> pendiente</span>}
                                        </strong>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    {appointments.length === 0 && <p style={{ color: '#64748b' }}>No se encontraron citas.</p>}
                </div>
            )}

            <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={isFullyPaid(selectedApt) ? '✅ Cita Pagada' : isPartiallyPaid(selectedApt) ? '💰 Cita con Abono' : 'Cobro de Cita'}>
                {selectedApt && (
                    <div>
                        {/* Encabezado del paciente */}
                        <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                            <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>{selectedApt.patient_name}</div>
                            <div style={{ color: '#64748b', fontSize: '0.85rem' }}>C.I: {selectedApt.patient_cedula || 'N/A'} &nbsp;|&nbsp; Cita: {selectedApt.date} {(selectedApt.start_time || '').substring(0,5)}</div>
                        </div>

                        {/* Servicios */}
                        <h4 style={{ margin: '0 0 0.5rem 0', color: '#475569' }}>Servicios</h4>
                        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem 0' }}>
                            {selectedApt.services_data?.map((srv, idx) => (
                                <li key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', borderBottom: '1px solid #f1f5f9' }}>
                                    <span>{srv.name}</span>
                                    <strong>${parseFloat(srv.price || 0).toFixed(2)}</strong>
                                </li>
                            ))}
                        </ul>

                        {/* Resumen financiero */}
                        <div style={{ backgroundColor: '#f1f5f9', padding: '1rem', borderRadius: '8px', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                                <span>Total del tratamiento:</span>
                                <strong>${totals.subtotal.toFixed(2)}</strong>
                            </div>
                            {parseFloat(selectedApt.advance_payment || 0) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', color: '#16a34a' }}>
                                    <span>Ya abonado:</span>
                                    <strong>-${parseFloat(selectedApt.advance_payment || 0).toFixed(2)}</strong>
                                </div>
                            )}
                            {discountAmount > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', color: '#ea580c' }}>
                                    <span>Descuento adicional:</span>
                                    <strong>-${discountAmount.toFixed(2)}</strong>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: '800', fontSize: '1.15rem', borderTop: '1px solid #cbd5e1', paddingTop: '0.5rem', marginTop: '0.4rem', color: totals.total === 0 ? '#16a34a' : '#991b1b' }}>
                                <span>{totals.total === 0 ? '✅ SALDO EN CERO' : 'PENDIENTE A PAGAR:'}  </span>
                                <span>${totals.total.toFixed(2)}</span>
                            </div>
                        </div>

                        {/* Modal info para PAGADA */}
                        {isFullyPaid(selectedApt) && (
                            <div style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                                <div style={{ fontSize: '2rem' }}>✅</div>
                                <div style={{ fontWeight: '700', color: '#15803d', fontSize: '1.1rem' }}>Esta cita está totalmente pagada</div>
                                <div style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.25rem' }}>Monto cancelado: ${parseFloat(selectedApt.advance_payment || 0).toFixed(2)}</div>
                            </div>
                        )}

                        {/* Modal cobro para ABONADO o POR COBRAR */}
                        {!isFullyPaid(selectedApt) && (
                            <div style={{ marginTop: '1rem' }}>
                                {isPartiallyPaid(selectedApt) && (
                                    <div style={{ backgroundColor: '#fef9c3', border: '1px solid #fde047', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem', color: '#713f12' }}>
                                        💳 Esta cita tiene un abono de <strong>${parseFloat(selectedApt.advance_payment || 0).toFixed(2)}</strong>. Puedes registrar el pago del saldo restante.
                                    </div>
                                )}
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Descuento extra en caja:</label>
                                    <input
                                        type="number"
                                        style={{ ...styles.input, width: '100%', fontSize: '1.2rem' }}
                                        value={discountAmount || ''}
                                        onChange={(e) => {
                                            const newDiscount = parseFloat(e.target.value) || 0;
                                            setDiscountAmount(newDiscount);
                                            setAmountReceived(getPaymentTotals(selectedApt, newDiscount).total);
                                        }}
                                        min="0"
                                        max={totals.subtotal - totals.advance}
                                    />
                                </div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Monto a Cobrar Hoy:</label>
                                    <input
                                        type="number"
                                        style={{ ...styles.input, width: '100%', fontSize: '1.2rem' }}
                                        value={amountReceived}
                                        onChange={(e) => setAmountReceived(parseFloat(e.target.value) || 0)}
                                        min="0"
                                        max={totals.total}
                                    />
                                </div>
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Método de Pago:</label>
                                    <select style={{ ...styles.input, width: '100%' }} value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                                        <option value="efectivo">Efectivo</option>
                                        <option value="transferencia">Transferencia</option>
                                        <option value="tarjeta">Tarjeta</option>
                                    </select>
                                </div>
                                <button style={styles.buttonPrimary} onClick={handleCharge}>
                                    {isPartiallyPaid(selectedApt) ? 'Registrar Abono Adicional' : 'Confirmar Cobro y Completar'}
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default Recepcion;
